/**
 * Debounce Composable
 * Provides debouncing functionality for search and other operations
 */

import { ref, watch, onUnmounted } from 'vue';

/**
 * Create a debounced ref
 * @param {*} value - Initial value
 * @param {number} delay - Debounce delay in milliseconds
 * @returns {Object} - { debouncedValue, setValue, clearDebounce }
 */
export const useDebounce = (value, delay = 300) => {
  const debouncedValue = ref(value);
  let timeoutId = null;

  const setValue = (newValue) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      debouncedValue.value = newValue;
    }, delay);
  };

  const clearDebounce = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  // Cleanup on unmount
  onUnmounted(() => {
    clearDebounce();
  });

  return {
    debouncedValue,
    setValue,
    clearDebounce,
  };
};

/**
 * Create a debounced function
 * @param {Function} fn - Function to debounce
 * @param {number} delay - Debounce delay in milliseconds
 * @returns {Function} - Debounced function
 */
export const useDebounceFn = (fn, delay = 300) => {
  let timeoutId = null;

  const debouncedFn = (...args) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      fn(...args);
    }, delay);
  };

  const cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  // Return debounced function with cancel method
  debouncedFn.cancel = cancel;

  return debouncedFn;
};

/**
 * Watch a ref with debouncing
 * @param {Ref} source - Ref to watch
 * @param {Function} callback - Callback function
 * @param {number} delay - Debounce delay in milliseconds
 * @returns {Function} - Stop watch function
 */
export const useDebounceWatch = (source, callback, delay = 300) => {
  let timeoutId = null;

  const stopWatch = watch(source, (newValue, oldValue) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      callback(newValue, oldValue);
    }, delay);
  });

  onUnmounted(() => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  });

  return stopWatch;
};

export default {
  useDebounce,
  useDebounceFn,
  useDebounceWatch,
};
